/**
 * `useLogout` — canonical sign-out flow for the DFQ SPAs.
 *
 * Returns a stable, zero-argument async function that:
 *
 *   1. Best-effort `POST /auth/logout` via the shared auth client to revoke
 *      the refresh token server-side. Server failures are swallowed — the
 *      user's intent is to leave, so client state must clear regardless.
 *   2. Clears the in-memory token store (`tokenStore.clearTokens()`).
 *   3. Removes the persisted user profile (`localStorage['dfq-user']`).
 *   4. Dispatches the `logout` thunk on the auth slice to reset Redux state.
 *      The thunk's `fulfilled` / `rejected` reducer re-runs steps 2 and 3
 *      as a safety net, so downstream callers see a fully-clean slice even
 *      if step 2/3 here throws on a locked-down browser (private mode,
 *      quota exceeded).
 *   5. Navigates to `/login` via React Router's `useNavigate()` — never via
 *      `window.location.href`, so the SPA transition plays nicely with the
 *      in-memory Redux store and any route-leaving guards.
 *
 * Used by:
 * - User-menu "Sign out" in `AdminLayout` (CON-AUTH-001) and
 *   `CustomerLayout` (SF-AUTH-010)
 * - `SessionExpiryWarning` "Sign out now" action (SF-AUTH-008)
 * - The 401 interceptor when token refresh itself fails
 *
 * The returned function is memoized with `useCallback` so consumers can
 * pass it as a `DropdownMenu` `onSelect` handler without triggering
 * spurious re-renders.
 *
 * SF-AUTH-009.
 */
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { clearTokens } from '@dfq/shared/auth/tokenStore';
import { useAppDispatch } from '@dfq/shared/stores/hooks';
import { logout as logoutThunk } from '@dfq/shared/stores/authSlice';

/** Storage key for the cached user profile — mirrors `authSlice`. */
const USER_STORAGE_KEY = 'dfq-user';

/**
 * Remove the persisted user profile from `localStorage`. Wrapped so the
 * hook stays resilient in private-browsing / quota-exceeded modes where
 * `removeItem` can throw — the thunk reducer repeats the cleanup anyway.
 */
function clearPersistedUser(): void {
  if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') {
    return;
  }
  try {
    window.localStorage.removeItem(USER_STORAGE_KEY);
  } catch {
    // best-effort — downstream thunk re-runs this cleanup
  }
}

/**
 * Hook returning a zero-arg `() => Promise<void>` that executes the full
 * sign-out sequence. Promise always resolves — server errors are swallowed
 * so local cleanup and navigation run regardless.
 */
export function useLogout(): () => Promise<void> {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  return useCallback(async (): Promise<void> => {
    // Dispatch the thunk first. Internally it:
    //   - awaits a best-effort `POST /auth/logout` (swallowing failures)
    //   - runs the `fulfilled` / `rejected` reducer, which clears tokens,
    //     resets slice state, and wipes the persisted user profile
    // We additionally clear the token store and persisted user here so
    // the guarantee holds even if the thunk itself is mocked / bypassed
    // in a specific consumer (tests, Storybook, interceptor path).
    try {
      await dispatch(logoutThunk()).unwrap();
    } catch {
      // thunk never rejects in practice — it swallows server errors —
      // but if an unexpected sync failure escapes, carry on with cleanup
    }

    // Belt-and-braces cleanup. Safe to call from a signed-out state.
    clearTokens();
    clearPersistedUser();

    // React Router transition. Use `replace` so the user cannot click
    // "back" into the authenticated shell after signing out.
    navigate('/login', { replace: true });
  }, [dispatch, navigate]);
}

/**
 * Exposed for tests that want to assert against the storage key without
 * re-declaring the constant.
 */
export const USE_LOGOUT_USER_STORAGE_KEY = USER_STORAGE_KEY;
